import React, {
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from "react";
import { useCtrlF, useEscapeKey } from "../../../Hooks";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  ClientSideRowModelModule,
} from "ag-grid-community";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useSelector } from "react-redux";

ModuleRegistry.registerModules([ClientSideRowModelModule, AllCommunityModule]);

const AgGridDataTable = forwardRef(
  (
    { rowData, colDefs, loading, handleKeyActions, onColumnMoved, contextRef },
    ref,
  ) => {
    const gridRef = useRef({ api: null, columnApi: null });
    const marketWatchRef = useRef([]);
    const focusedRowIndexRef = useRef(null);
    const prevRowDataLengthRef = useRef(rowData.length);
    const isInitialLoadRef = useRef(true);
    const { marketActiveTab } = useSelector((state) => state.market);

    const sizeColumnsToFit = useCallback(() => {
      const api = gridRef.current?.api;
      if (api) {
        api.sizeColumnsToFit();
      }
    }, []);

    const handleFullScreenTable = () => {
      const elem = marketWatchRef.current;
      if (!elem) return;

      try {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen();
        }
      } catch (error) {
        console.log("Fullscreen error:", error);
      }
    };

    useCtrlF(() => {
      handleFullScreenTable();
    });

    useEffect(() => {
      const handleFullscreenChange = () => {
        setTimeout(() => {
          sizeColumnsToFit();
        }, 100);
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.addEventListener("msfullscreenchange", handleFullscreenChange);

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener(
          "msfullscreenchange",
          handleFullscreenChange,
        );
      };
    }, [sizeColumnsToFit]);

    const onGridReady = useCallback(
      (params) => {
        gridRef.current.api = params.api;
        gridRef.current.columnApi = params.columnApi;
        sizeColumnsToFit();
      },
      [sizeColumnsToFit],
    );

    useEffect(() => {
      if (gridRef.current?.api) {
        sizeColumnsToFit();
      }
    }, [colDefs, sizeColumnsToFit]);

    const getLatestFocusedRow = useCallback(() => {
      const api = gridRef.current?.api;
      const focusedCell = api?.getFocusedCell();
      return focusedCell
        ? api.getDisplayedRowAtIndex(focusedCell.rowIndex)?.data
        : null;
    }, []);

    useImperativeHandle(ref, () => ({
      api: gridRef.current?.api,
      columnApi: gridRef.current?.columnApi,
      getLatestFocusedRow,
    }));

    const getRowId = (params) =>
      params.data?.ScriptToken?.toString() || Math.random().toString();

    const defaultColDef = useMemo(
      () => ({
        sortable: false,
        filter: false,
        resizable: true,
        movable: true,
      }),
      [],
    );

    const onKeyDown = (event) => {
      const api = gridRef.current?.api;
      let rowIndex = focusedRowIndexRef.current;

      if (rowIndex === null || rowIndex === undefined) {
        const focusedCell = api?.getFocusedCell();
        if (!focusedCell) return;
        rowIndex = focusedCell.rowIndex;
      }

      const rowNode = api.getDisplayedRowAtIndex(rowIndex);
      const currentRowData = rowNode?.data;
      if (!currentRowData) return;

      const moveRowByToken = (token, direction) => {
        const currentIndex = rowData.findIndex(
          (row) => row.ScriptToken === token,
        );

        if (currentIndex === -1) return;

        const newIndex = currentIndex + direction;
        if (newIndex < 0 || newIndex >= rowData.length) return;

        const newData = [...rowData];
        const [movedRow] = newData.splice(currentIndex, 1);
        newData.splice(newIndex, 0, movedRow);

        handleKeyActions?.({
          action: "rowReorder",
          updatedRows: newData,
        });

        focusedRowIndexRef.current = newIndex;
      };

      // ALT shortcuts
      if (event.altKey && event.key.toLowerCase() === "c") {
        handleKeyActions?.({ row: currentRowData, action: "view chart" });
        event.preventDefault();
        return;
      } else if (event.altKey && event.key.toLowerCase() === "i") {
        handleKeyActions?.({ row: currentRowData, action: "info" });
        event.preventDefault();
        return;
      }

      if (marketActiveTab === null || marketActiveTab === "all") {
        switch (event.key) {
          case "Delete":
            handleKeyActions?.({ row: currentRowData, action: "delete" });
            setTimeout(() => {
              api.getDisplayedRowAtIndex(rowIndex)?.setSelected(true, true);
            }, 0);
            event.preventDefault();
            break;

          case " ":
            handleKeyActions?.({ row: currentRowData, action: "space" });
            setTimeout(() => {
              const nextIndex = rowIndex + 1;
              const firstField = colDefs[0]?.field;
              if (firstField) {
                api.ensureIndexVisible(nextIndex);
                api.setFocusedCell(nextIndex, firstField);
                api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
                focusedRowIndexRef.current = nextIndex;
              }
            }, 0);
            event.preventDefault();
            break;

          case "ArrowUp":
            if (event.ctrlKey) {
              const firstField = colDefs[0]?.field;
              if (firstField) {
                api.ensureIndexVisible(0);
                api.setFocusedCell(0, firstField);
                api.getDisplayedRowAtIndex(0)?.setSelected(true, true);
                focusedRowIndexRef.current = 0;
              }
              event.preventDefault();
            } else if (event.shiftKey) {
              moveRowByToken(currentRowData.ScriptToken, -1);

              event.preventDefault();
            }
            break;

          case "ArrowDown":
            if (event.ctrlKey) {
              const lastIndex = rowData.length - 1;
              const firstField = colDefs[0]?.field;
              if (firstField) {
                api.ensureIndexVisible(lastIndex);
                api.setFocusedCell(lastIndex, firstField);
                api.getDisplayedRowAtIndex(lastIndex)?.setSelected(true, true);
                focusedRowIndexRef.current = lastIndex;
              }
              event.preventDefault();
            } else if (event.shiftKey) {
              moveRowByToken(currentRowData.ScriptToken, +1);
              event.preventDefault();
            }

            break;

          default:
            break;
        }
      }
    };

    const suppressKeyboardEvent = useCallback((params) => {
      return ["Delete", "d", "D", " "].includes(params.event.key);
    }, []);

    const onCellFocused = useCallback((event) => {
      const api = gridRef.current?.api;
      const rowIndex = event.rowIndex;
      if (rowIndex != null) {
        focusedRowIndexRef.current = rowIndex;
        api.getDisplayedRowAtIndex(rowIndex)?.setSelected(true, true);
      }
    }, []);

    const onColumnMovedHandler = useCallback(() => {
      const api = gridRef.current?.api;
      const allCols = api.getAllGridColumns();
      const colState = allCols
        .map((col) => {
          const colDef = colDefs.find((c) => c.field === col.getColDef().field);
          return colDef ? { ...colDef } : null;
        })
        .filter(Boolean);
      onColumnMoved?.(colState);
    }, [colDefs, onColumnMoved]);

    useEscapeKey(() => {
      const api = gridRef.current?.api;
      const firstField = colDefs[0]?.field;
      if (firstField && rowData?.length) {
        api.ensureIndexVisible(0);
        api.setFocusedCell(0, firstField);
        api.getDisplayedRowAtIndex(0)?.setSelected(true, true);
        focusedRowIndexRef.current = 0;
      }
    });

    // useEffect(() => {
    //   if (gridRef.current?.api) {
    //     const prevLength = prevRowDataLengthRef.current;
    //     const newLength = rowData.length;

    //     if (newLength > prevLength) {
    //       const api = gridRef.current.api;
    //       const firstField = colDefs[0]?.field;

    //       if (!firstField) return;

    //       const focusedCell = api.getFocusedCell();
    //       const focusedRowIndex = focusedCell?.rowIndex;

    //       const lastIndex = newLength - 1;
    //       if (focusedRowIndex === undefined || focusedRowIndex === null) {
    //         api.ensureIndexVisible(lastIndex);
    //         api.setFocusedCell(lastIndex, firstField);
    //         api.getDisplayedRowAtIndex(lastIndex)?.setSelected(true, true);
    //         focusedRowIndexRef.current = lastIndex;
    //       } else if (focusedRowIndex === lastIndex) {
    //         api.ensureIndexVisible(lastIndex);
    //         api.setFocusedCell(lastIndex, firstField);
    //         api.getDisplayedRowAtIndex(lastIndex)?.setSelected(true, true);
    //         focusedRowIndexRef.current = lastIndex;
    //       } else {
    //         const nextIndex = focusedRowIndex + 1;
    //         api.ensureIndexVisible(nextIndex);
    //         api.setFocusedCell(nextIndex, firstField);
    //         api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
    //         focusedRowIndexRef.current = nextIndex;
    //       }
    //     }

    //     prevRowDataLengthRef.current = newLength;
    //   }
    // }, [rowData, colDefs]);

    useEffect(() => {
      if (!gridRef.current?.api || !rowData.length) return;

      const api = gridRef.current.api;
      const firstField = colDefs[0]?.field;
      if (!firstField) return;

      // INITIAL LOAD → focus FIRST row
      if (isInitialLoadRef.current) {
        api.ensureIndexVisible(0);
        api.setFocusedCell(0, firstField);
        api.getDisplayedRowAtIndex(0)?.setSelected(true, true);
        focusedRowIndexRef.current = 0;

        isInitialLoadRef.current = false;
        prevRowDataLengthRef.current = rowData.length;
        return;
      }

      //  EXISTING behavior for row additions (UNCHANGED)
      const prevLength = prevRowDataLengthRef.current;
      const newLength = rowData.length;

      if (newLength > prevLength) {
        const focusedCell = api.getFocusedCell();
        const focusedRowIndex = focusedCell?.rowIndex;

        const lastIndex = newLength - 1;

        if (focusedRowIndex == null) {
          api.ensureIndexVisible(lastIndex);
          api.setFocusedCell(lastIndex, firstField);
          api.getDisplayedRowAtIndex(lastIndex)?.setSelected(true, true);
          focusedRowIndexRef.current = lastIndex;
        } else {
          const nextIndex = Math.min(focusedRowIndex + 1, lastIndex);
          api.ensureIndexVisible(nextIndex);
          api.setFocusedCell(nextIndex, firstField);
          api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
          focusedRowIndexRef.current = nextIndex;
        }
      }

      prevRowDataLengthRef.current = newLength;
    }, [rowData, colDefs]);

    const onCellContextMenu = useCallback((params) => {
      params.event.preventDefault();
      contextRef?.current?.showContextMenu({
        visible: true,
        x: params.event.clientX,
        y: params.event.clientY,
        rowData: params.node?.data,
      });
    }, []);

    return (
      <>
        <div
          className="ag-theme-alpine"
          style={{
            height: 650,
            width: "100%",
          }}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onContextMenu={(e) => e.preventDefault()} // prevent default browser context menu
          ref={marketWatchRef}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={colDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            getRowId={getRowId}
            rowSelection="single"
            suppressRowClickSelection={true}
            suppressKeyboardEvent={suppressKeyboardEvent}
            suppressRowDeselection={true}
            animateRows={false}
            rowBuffer={5}
            rowDragManaged={true}
            suppressMoveWhenRowDragging={false}
            onGridReady={onGridReady}
            onColumnMoved={onColumnMovedHandler}
            onCellFocused={onCellFocused}
            onCellContextMenu={onCellContextMenu}
            suppressDragLeaveHidesColumns={true}
            ensureDomOrder={true}
            tooltipShowDelay={500}
            tooltipMouseTrack={true}
            rowHeight={30}
          />
        </div>
      </>
    );
  },
);

export default AgGridDataTable;
